import { useEffect, useState } from "react";
import { Check, Loader2, PartyPopper, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Acompanha o processamento REAL: as etapas avançam enquanto a chamada roda e a
 * última só é concluída quando `done` chega. Nenhuma espera artificial longa.
 */
export function ProcessingOverlay({
  title,
  steps = [],
  done = false,
  error = "",
  successTitle,
  successText,
  onFinish,
  onRetry,
  onClose,
  testid = "processing-overlay",
}) {
  const [step, setStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (error) return;
    // Sem resposta ainda: para na penúltima etapa em vez de fingir conclusão.
    const limit = done ? steps.length : Math.max(steps.length - 1, 0);
    if (step >= limit) return;
    const t = setTimeout(() => setStep((s) => s + 1), 420);
    return () => clearTimeout(t);
  }, [step, done, error, steps.length]);

  useEffect(() => {
    if (!done || error || step < steps.length) return;
    setShowSuccess(true);
    const t = setTimeout(() => onFinish?.(), 900);
    return () => clearTimeout(t);
  }, [done, error, step, steps.length, onFinish]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/85 px-5 backdrop-blur-sm"
      data-testid={testid}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-card p-6 shadow-2xl">
        {error ? (
          <div className="text-center" data-testid="processing-error">
            <XCircle className="mx-auto h-7 w-7 text-destructive" />
            <p className="mt-3 font-display text-lg">Não conseguimos concluir</p>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
            <div className="mt-5 flex flex-col gap-2">
              {onRetry && (
                <Button
                  onClick={onRetry}
                  data-testid="processing-retry-btn"
                  className="h-11 rounded-full font-semibold"
                >
                  Tentar de novo
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                data-testid="processing-close-btn"
                className="h-11 rounded-full"
              >
                Voltar
              </Button>
            </div>
          </div>
        ) : showSuccess ? (
          <div className="text-center" data-testid="processing-success">
            <PartyPopper className="mx-auto h-7 w-7 text-emerald-300" />
            <p className="mt-3 font-display text-xl">{successTitle}</p>
            <p className="mt-1 text-sm text-muted-foreground">{successText}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-brand" />
              <p className="font-display text-lg">{title}</p>
            </div>
            <ol className="mt-4 space-y-2.5">
              {steps.map((label, i) => (
                <li
                  key={label}
                  className="flex items-center gap-3 text-sm"
                  data-testid={`processing-step-${i}`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                      i < step
                        ? "border-emerald-500/50 bg-emerald-500/20 text-emerald-300"
                        : i === step
                        ? "border-brand bg-brand/20 text-brand"
                        : "border-white/15 text-muted-foreground"
                    }`}
                  >
                    {i < step ? (
                      <Check className="h-3 w-3" />
                    ) : i === step ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : null}
                  </span>
                  <span className={i <= step ? "text-foreground" : "text-muted-foreground"}>
                    {label}
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}
